import { useQuery } from '@tanstack/react-query'
import { ExternalLink, FileText, Image as ImageIcon, Link2, ListChecks, ThumbsUp } from 'lucide-react'
import { AttachmentList } from '@/components/composites/attachment-list'
import { UserAvatar } from '@/components/composites/user-avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDueShort } from '@/lib/dates'
import type { Card } from '@/types/entities'
import { reviewPackApi, reviewPackKey } from './api'

/**
 * The first thing an approver sees. Approve lives inside it, so signing off and having looked
 * are the same gesture. An item that reached approval before packs existed has none — that is
 * said plainly, and approving is still possible rather than stranding it.
 */
export function ReviewPackDialog({
  card,
  open,
  onOpenChange,
  onApprove,
  busy = false,
  approveLabel = 'Approve',
}: {
  card: Card
  open: boolean
  onOpenChange: (open: boolean) => void
  onApprove: () => void
  busy?: boolean
  approveLabel?: string
}) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: reviewPackKey(card.id),
    queryFn: () => reviewPackApi.get(card.id),
    enabled: open,
    staleTime: 0,
  })
  const pack = data?.pack ?? null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Review before you approve</DialogTitle>
          <DialogDescription>“{card.title}”</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="grid gap-3"><Skeleton className="h-16" /><Skeleton className="h-24" /><Skeleton className="h-16" /></div>
        ) : isError ? (
          <p className="rounded-md border border-danger/40 bg-danger-soft/40 p-3 text-sm">
            Could not load what was shared. <button className="font-medium underline" onClick={() => void refetch()}>Try again</button>
          </p>
        ) : !pack ? (
          <p className="rounded-md border border-border bg-muted p-3 text-sm text-text-secondary">
            Nothing was shared with this one — it was sent for approval before review packs existed. Open the work item to see what it holds.
          </p>
        ) : (
          <div className="grid gap-4">
            {data?.submitter ? (
              <div className="flex items-center gap-2.5">
                <UserAvatar person={data.submitter} size="md" />
                <div className="text-sm">
                  <span className="font-medium">{data.submitter.displayName}</span> shared this
                  <div className="text-tiny text-text-muted">
                    {formatDueShort(pack.submittedAt)}
                    {data.previous > 0 ? ` · resubmitted (${data.previous} earlier ${data.previous === 1 ? 'version' : 'versions'})` : ''}
                  </div>
                </div>
              </div>
            ) : null}

            <section>
              <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold"><ListChecks className="size-4 text-brand" /> What was done</h3>
              <ul className="grid gap-1.5 rounded-lg border border-border bg-card p-3">
                {pack.bullets.map((b, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand" aria-hidden />
                    <span className="min-w-0 break-words">{b}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold"><ImageIcon className="size-4 text-brand" /> Photo</h3>
              <AttachmentList kind="review" itemId={card.id} attachments={[pack.photo]} />
            </section>

            <section>
              <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold"><FileText className="size-4 text-brand" /> Document</h3>
              {pack.document ? <AttachmentList kind="review" itemId={card.id} attachments={[pack.document]} /> : null}
              {pack.documentLink ? (
                <a href={pack.documentLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5 text-sm transition-colors dur-fast hover:bg-muted">
                  <span className="min-w-0 flex-1 truncate text-brand" title={pack.documentLink}>{pack.documentLink}</span>
                  <ExternalLink className="size-3.5 shrink-0 text-text-muted" />
                </a>
              ) : null}
              {pack.writeUp ? <p className="whitespace-pre-wrap break-words rounded-lg border border-border bg-card p-3 text-sm">{pack.writeUp}</p> : null}
            </section>

            <section>
              <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold"><Link2 className="size-4 text-brand" /> Link</h3>
              <a
                href={pack.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5 text-sm transition-colors dur-fast hover:bg-muted"
              >
                <span className="min-w-0 flex-1 truncate text-brand" title={pack.link}>{pack.link}</span>
                <ExternalLink className="size-3.5 shrink-0 text-text-muted" />
              </a>
            </section>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button type="button" className="gap-1.5" disabled={busy || isLoading} onClick={onApprove}>
            <ThumbsUp className="size-3.5" /> {busy ? 'Approving…' : approveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
